/* eslint-disable @typescript-eslint/no-explicit-any */
import { Row, Col } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { NewReturningData } from 'data/newreturning';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import returnOutService from 'service/returnOut';
import returnService from 'service/return';
import Button from 'components/base/Button';
import SupperModal from 'components/modals/SupperModal';
import legalEntitySettingsService from 'service/legalEntitySettings';

interface NewReturningDataTableProps {
  setOpenFolder: (show: boolean) => void;
}
const NewReturningDataTable = ({
  setOpenFolder
}: NewReturningDataTableProps) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const legalEntityName = useSelector(
    (state: any) => state?.auth?.legal_entity?.name
  );

  const [openSupperModal, setOpenSupperModal] = useState(false);
  const [confirmToDelete, setConfirmToDelete] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  const { data, refetch } = useQuery(['GET_RETURN_OUT'], async () => {
    const res = await returnOutService
      .getList({
        offset: 0,
        limit: 100,
        // status_id: 'd9f0bc1f-5245-428a-800b-d51aadde2557',
        status_pid: 13
      })
      .then((res: any) => {
        return res?.returns;
      });
    return res;
  });

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const tableData: NewReturningData[] = useMemo(() => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    const resultData =
      data?.map((items: any) => {
        return {
          orders: items?.return_number,
          sender: legalEntityName,
          recipient: items?.legal_entity?.name,
          currency: items?.currency?.name?.ru,
          currency_pid: items?.currency?.pid || 0,
          date_create: formatDateToTimeZone(items?.date_create, timeZone),
          recipient_id: items?.recipient_id,
          recipient_pid: items?.recipient_pid || 0,
          guid: items?.id,
          gupid: items?.pid
        };
      }) ?? [];

    return resultData;
  }, [data, dataSettings]);

  const handleDelete = (id: string) => {
    setSelectedInvoiceId(id);
    setOpenSupperModal(true);
  };

  const newApplicationsTableColumns: ColumnDef<NewReturningData>[] = [
    {
      id: 'action',
      cell: rowData => {
        return (
          <Row>
            <Col className="d-flex gap-2">
              <Button
                variant="hover"
                className="p-2"
                onClick={() => {
                  navigate(
                    `/returning/outgoing/filter/newreturning/${rowData?.row?.original?.guid}?sender_id=${rowData?.row?.original?.recipient_id}`,
                    {
                      state: {
                        newreturning_pid: rowData?.row?.original?.gupid,
                        recipient_pid: rowData?.row?.original?.recipient_pid,
                        currency_pid: rowData?.row?.original?.currency_pid
                      }
                    }
                  );
                  setOpenFolder(false);
                }}
              >
                <FeatherIcon icon="plus" className="cursor-pointer" size={20} />
              </Button>
              <Button
                variant="hover"
                className="p-2"
                onClick={() => handleDelete(rowData?.row?.original?.guid)}
              >
                <FeatherIcon
                  icon="trash-2"
                  className="cursor-pointer text-danger"
                  size={20}
                />
              </Button>
            </Col>
          </Row>
        );
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-end' }
      }
    },

    {
      accessorKey: 'orders',
      header: t('order_number'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'sender',
      header: t('sender'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'recipient',
      header: t('recipient'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'date_create',
      header: t('date_create'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: tableData,
    columns: newApplicationsTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if (selectedInvoiceId && confirmToDelete) {
      returnService.deleteReturn(selectedInvoiceId).finally(() => {
        refetch();
        setOpenSupperModal(false);
        setConfirmToDelete(false);
        setSelectedInvoiceId(null);
      });
    }
  }, [confirmToDelete, selectedInvoiceId, refetch]);

  return (
    <AdvanceTableProvider {...table}>
      <div>
        <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
        <AdvanceTableFooter pagination />
      </div>

      <SupperModal
        show={openSupperModal}
        setShow={setOpenSupperModal}
        setConfirm={setConfirmToDelete}
        title={t('product')}
        bodyText={t('you_want_delete')}
      />
    </AdvanceTableProvider>
  );
};

export default NewReturningDataTable;
